import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import type {
  ComponentSpec,
  WidgetResponseSpec,
  DashboardDataResponseSpec,
  BackendDashboardSpec,
} from "../Processing/types";
import type { GridWidgetType } from "../types";
import type { MetricOption, SubjectEntity } from "../Processing/types";
import { getMockSpec, getMockResponse, getMockResponseForSpec } from "../DataMocks";
import { USE_MOCK_DATA } from "../constants";

import { trainingAPI } from "../../../api/TrainingAPI";
import { useDataAccess } from "@components/PermissionedComponents";
import { fetchDashboardSpec, type DashboardSpecResult } from "./fetchDashboardSpec";
import { fetchDashboardData } from "./fetchDashboardData";
import { streamDashboardInsights } from "./fetchDashboardInsights";
import type { AiStreamFrame, AiStreamOutcome } from "./fetchDashboardInsights";
import { fetchWidgetSpec } from "./fetchWidgetSpec";
import { fetchWidgetData } from "./fetchWidgetData";
import { fetchMetrics } from "./fetchMetrics";
import { fetchSubjectEntities } from "./fetchSubjectEntities";


export interface DataPipeline {
  getDashboardSpec: (dashboardId: string) => Promise<DashboardSpecResult>;
  getDashboardData: (dashboardId: string) => Promise<DashboardDataResponseSpec>;
  streamAiWidget: (
    args: {
      dashboardId: string;
      dashboardSpec: BackendDashboardSpec;
      dashboardData: DashboardDataResponseSpec;
      widgetId: string;
      force: boolean;
    },
    onFrame: (frame: AiStreamFrame) => void,
    signal: AbortSignal,
  ) => Promise<AiStreamOutcome>;
  getWidgetSpec: (dashboardId: string, widgetId: string) => Promise<ComponentSpec>;
  getWidgetData: (dashboardId: string, widgetId: string) => Promise<WidgetResponseSpec | null>;
  saveDashboard: (payload: Record<string, unknown>) => Promise<void>;
  getMetrics: () => MetricOption[];
  getSubjectEntities: (subjectType: string) => SubjectEntity[];
  resolveSpec: (type: GridWidgetType) => ComponentSpec;
  resolveResponse: (type: GridWidgetType, widgetId: string) => WidgetResponseSpec | null;
  resolveResponseForSpec: (spec: ComponentSpec, widgetId: string) => WidgetResponseSpec | null;
  useMocks: boolean;
}

/**
 * Packages every data-fetching and resolution function into a single hook.
 *
 * When the backend is ready:
 *   1. Set USE_MOCKS to false (or make it configurable via prop/env).
 *   2. The async functions already delegate to the real fetch* calls —
 *      just replace the placeholder bodies in each file.
 *   3. getMetrics / getSubjectEntities will switch from returning
 *      synchronous mock data to returning cached results populated
 *      by an initial fetch on provider mount.
 */
const METRIC_CATEGORIES = ["gps", "testing", "forceplate", "wellness", "nutrition", "cognition"] as const;

export function useDataPipeline(): DataPipeline {
  const allPlayers: any[] = useSelector(({ team }: any) => team.allPlayers);
  const positionEntities: { id: string; name: string }[] = useSelector(({ team }: any) => team.positionEntities);
  const drills: any[] = useSelector(({ team }: any) => team.drills);
  const playerGroups: any[] = useSelector(({ team }: any) => team.playerGroups);
  const allMetricTypes: Record<string, any[]> = useSelector(({ team }: any) => team.all_metric_types);
  // The helmet metrics sit apart from all_metric_types (see team.reducer.js).
  const helmetMetrics: any[] = useSelector(({ team }: any) => team.helmet_metrics);

  // Metrics the user cannot access are dropped from the catalogue entirely.
  const { canAccess } = useDataAccess();

  const getDashboardSpec = useCallback(
    (dashboardId: string) => fetchDashboardSpec(dashboardId),
    [],
  );

  const getDashboardData = useCallback(
    (dashboardId: string) => fetchDashboardData(dashboardId),
    [],
  );

  const streamAiWidget = useCallback(streamDashboardInsights, []);

  const getWidgetSpec = useCallback(
    (dashboardId: string, widgetId: string) => fetchWidgetSpec(dashboardId, widgetId),
    [],
  );

  const getWidgetDataAsync = useCallback(
    (dashboardId: string, widgetId: string) => fetchWidgetData(dashboardId, widgetId),
    [],
  );

  const getMetrics = useCallback((): MetricOption[] => {
    const measured: MetricOption[] = METRIC_CATEGORIES.filter((cat) => canAccess(cat as any)).flatMap((cat) =>
      (allMetricTypes?.[cat] ?? []).map((m: any) => ({
        id: m.unique_identifier as string,
        name: m.name as string,
        category: cat === "gps" ? "GPS" : cat.charAt(0).toUpperCase() + cat.slice(1),
        metricKey: m.key as string | undefined,
        isAsym: (m.metadata?.is_in_asym_group ?? false) === true && (m.metadata?.side ?? "") === "A",
      })),
    );
    // A helmet count has no best, so each helmet option carries its aggregations.
    const helmet: MetricOption[] = canAccess("helmet")
      ? (helmetMetrics ?? []).map((m: any) => ({
          id: m.unique_identifier as string,
          name: m.name as string,
          category: "Helmet",
          metricKey: m.key as string | undefined,
          aggregations: m.aggregations as string[] | undefined,
        }))
      : [];
    return [...measured, ...helmet];
  }, [allMetricTypes, helmetMetrics, canAccess]);

  const getSubjectEntities = useCallback(
    (subjectType: string): SubjectEntity[] => {
      switch (subjectType) {
        case "player":
          return allPlayers
            .filter((p) => p.retired === false)
            .map((p) => ({
              id: String(p.player_id),
              name: `${p.first_name} ${p.last_name}`,
              detail: p.position ?? "",
            }));
        case "position":
          return positionEntities.map((pos) => ({
            id: pos.id,
            name: pos.name,
            detail: "",
          }));
        case "drill":
          return drills.map((d) => ({
            id: String(d.drill_id),
            name: d.name,
            detail: "",
          }));
        case "group":
          return (playerGroups ?? []).map((g) => ({
            id: String(g.player_group_id),
            name: g.name,
            detail: "",
          }));
        default:
          return [];
      }
    },
    [allPlayers, positionEntities, drills, playerGroups],
  );

  const saveDashboard = useCallback(
    async (payload: Record<string, unknown>): Promise<void> => {
      // eslint-disable-next-line no-console
      console.log("[DataPipeline] saveDashboard:", payload);
      const dashboardId = payload.id as string | undefined;
      if (!dashboardId) return;
      const validation = await trainingAPI.validateDashboardSchema(payload);
      if (!validation.is_valid) {
        // eslint-disable-next-line no-console
        console.warn("[DataPipeline] schema validation failed:", validation.message);
        throw new Error(
          typeof validation.message === "string"
            ? validation.message
            : "Dashboard schema validation failed",
        );
      }
      await trainingAPI.patchDashboard(dashboardId, { info: payload });
    },
    [],
  );

  const resolveSpec = useCallback((type: GridWidgetType): ComponentSpec => {
    if (USE_MOCK_DATA) return getMockSpec(type);
    return getMockSpec(type);
  }, []);

  const resolveResponse = useCallback(
    (type: GridWidgetType, widgetId: string): WidgetResponseSpec | null => {
      if (USE_MOCK_DATA) return getMockResponse(type, widgetId);
      return null;
    },
    [],
  );

  const resolveResponseForSpec = useCallback(
    (spec: ComponentSpec, widgetId: string): WidgetResponseSpec | null => {
      if (USE_MOCK_DATA) return getMockResponseForSpec(spec, widgetId);
      return null;
    },
    [],
  );

  return useMemo<DataPipeline>(
    () => ({
      getDashboardSpec,
      getDashboardData,
      streamAiWidget,
      getWidgetSpec,
      getWidgetData: getWidgetDataAsync,
      saveDashboard,
      getMetrics,
      getSubjectEntities,
      resolveSpec,
      resolveResponse,
      resolveResponseForSpec,
      useMocks: USE_MOCK_DATA,
    }),
    [
      getDashboardSpec,
      getDashboardData,
      streamAiWidget,
      getWidgetSpec,
      getWidgetDataAsync,
      saveDashboard,
      getMetrics,
      getSubjectEntities,
      resolveSpec,
      resolveResponse,
      resolveResponseForSpec,
    ],
  );
}
